import { useMemo, useState } from "react";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format, isValid, parseISO } from "date-fns";
import { useWatch } from "react-hook-form";
import { useInput, useTranslate } from "react-admin";

const toDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return isValid(value) ? value : null;

  const parsed = parseISO(value);
  return isValid(parsed) ? parsed : null;
};

const toInputValue = (value) => {
  if (!value || !isValid(value)) return "";
  return format(value, "yyyy-MM-dd");
};

const MuiDatePickerInput = ({
  source,
  minDateSource,
  maxDateSource,
  label,
  helperText,
  variant = "outlined",
  margin,
  size,
  fullWidth,
  onChange: externalOnChange,
  disabled,
  textFieldProps,
  slotProps,
  ...rest
}) => {
  const translate = useTranslate();
  const resolvedMargin =
    margin ?? (helperText === false ? "dense" : "normal");
  const noMinDateSource = "__mui_datepicker_no_min_date_source__";
  const noMaxDateSource = "__mui_datepicker_no_max_date_source__";
  const [validationError, setValidationError] = useState(null);
  const {
    field,
    fieldState: { error },
    id,
    isRequired,
  } = useInput({ source, ...rest });

  const value = useMemo(() => toDate(field.value), [field.value]);
  const minDateValue = useWatch({ name: minDateSource || noMinDateSource });
  const maxDateValue = useWatch({ name: maxDateSource || noMaxDateSource });
  const minDate = useMemo(() => toDate(minDateValue), [minDateValue]);
  const maxDate = useMemo(() => toDate(maxDateValue), [maxDateValue]);

  const handleChange = (date, context) => {
    if (context?.validationError) return;

    setValidationError(null);
    const nextValue = toInputValue(date);

    if (nextValue === field.value) return;

    field.onChange(nextValue);

    if (externalOnChange) externalOnChange(nextValue);
  };

  const mergedSlotProps = {
    ...slotProps,
    textField: {
      ...textFieldProps,
      ...(slotProps?.textField || {}),
      id,
      name: field.name,
      label: label ? translate(label) : undefined,
      required: isRequired,
      variant,
      size,
      fullWidth,
      margin: resolvedMargin,
      error: !!error || !!validationError,
      helperText:
        error?.message ||
        (validationError ? "Tanggal tidak valid" : helperText ?? " "),
      onBlur: field.onBlur,
    },
    field: {
      clearable: !disabled,
      ...(slotProps?.field || {}),
    },
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <DatePicker
        value={value}
        onChange={handleChange}
        onError={setValidationError}
        format="MM/dd/yyyy"
        disabled={disabled}
        disableOpenPicker={disabled}
        minDate={minDate}
        maxDate={maxDate}
        slotProps={mergedSlotProps}
      />
    </LocalizationProvider>
  );
};

export default MuiDatePickerInput;
